import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { BaseTimePeriod } from '@services/FiltersTypes';
import useDebouncedEffect from '@hooks/UseDebouncedEffect';
import {
  baseTimePeriodToDate,
  formatRangeBaseTimePeriods,
  getPeriodKey,
  getPreviousMonth,
  getPreviousWeek,
  isCustomWeeks,
} from '@utils/PeriodUtils';
import { useDateRangePickerCallBack, useDateRangePickerHandlerCallback } from './UseDateRangePickerCallback';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { Option } from '@utils/pageConfig/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getUnavailablePGsByPeriod } from '@utils/productGroupUtils/ProductGroupUtils';
import { elements } from '@ecosystem/newron-design-system';
import RangePicker from '@components/range-picker/RangePicker';

export type FocusPeriodProps = {
  periodOptions: BaseTimePeriod[];
  selectedPeriod: BaseTimePeriod;
  setSelectedPeriod: Dispatch<SetStateAction<BaseTimePeriod>>;
  selectedComparisonPeriod: BaseTimePeriod;
  setSelectedComparisonPeriod: Dispatch<SetStateAction<BaseTimePeriod>>;
  periodicity: Periodicity;
  isPeriodOptionsDisabled: boolean;
  appliedCountries?: Option[];
  appliedProductGroups?: Option[];
};

const CustomPeriod: React.FC<FocusPeriodProps> = ({
  periodOptions,
  selectedPeriod,
  setSelectedPeriod,
  selectedComparisonPeriod,
  setSelectedComparisonPeriod,
  periodicity,
  isPeriodOptionsDisabled,
  appliedCountries,
  appliedProductGroups,
}) => {
  const pagesConfig = usePageConfig();
  getUnavailablePGsByPeriod(
    selectedPeriod.value,
    periodicity,
    pagesConfig.pgsByCountry,
    pagesConfig.masterPgsByCountry,
    appliedCountries,
    appliedProductGroups,
  ).length > 0
    ? pagesConfig.setIsAllPgsByPeriodicityAvailable(false)
    : pagesConfig.setIsAllPgsByPeriodicityAvailable(true);

  const { Icon } = elements;
  const { Fractional } = Icon.icons;
  const [isSomePgsUnavailable, setIsSomePgsUnavailable] = useState<number>(0);

  const {
    startPeriod: periodFrom,
    endPeriod: periodTo,
    setStartPeriod: setPeriodFrom,
    setEndPeriod: setPeriodTo,
  } = useDateRangePickerCallBack(selectedPeriod, periodicity);

  const {
    startPeriod: comparisonPeriodFrom,
    endPeriod: comparisonPeriodTo,
    setStartPeriod: setComparisonPeriodFrom,
    setEndPeriod: setComparisonPeriodTo,
  } = useDateRangePickerCallBack(selectedComparisonPeriod, periodicity);

  const useDateRangePickerHandlerCallbackParam = {
    periodFrom,
    periodTo,
    comparisonPeriodFrom,
    comparisonPeriodTo,
    setPeriodFrom,
    setPeriodTo,
    setComparisonPeriodFrom,
    setComparisonPeriodTo,
    periodicity,
  };

  const { setPeriodFromHandler, setPeriodToHandler, setComparisonPeriodFromHandler, setComparisonPeriodToHandler } =
    useDateRangePickerHandlerCallback(useDateRangePickerHandlerCallbackParam);

  const minFocusPeriodFrom = useMemo(() => {
    return isCustomWeeks(periodicity)
      ? getPreviousWeek(baseTimePeriodToDate(periodOptions[1]), -1)
      : getPreviousMonth(baseTimePeriodToDate(periodOptions[1]), -1);
  }, [periodOptions, periodicity]);

  const maxComparisonPeriodTo = useMemo(() => {
    return isCustomWeeks(periodicity) ? getPreviousWeek(periodTo, 1) : getPreviousMonth(periodTo, 1);
  }, [periodTo, periodicity]);

  useDebouncedEffect(() => {
    const newSelectedPeriod = formatRangeBaseTimePeriods(
      periodFrom,
      periodTo,
      periodOptions[0].lastUpdatedDate,
      isCustomWeeks(periodicity),
    );
    const newSelectedComparisonPeriod = formatRangeBaseTimePeriods(
      comparisonPeriodFrom,
      comparisonPeriodTo,
      periodOptions[0].lastUpdatedDate,
      isCustomWeeks(periodicity),
    );
    setSelectedPeriod(newSelectedPeriod);
    setSelectedComparisonPeriod(newSelectedComparisonPeriod);

    setIsSomePgsUnavailable(
      getUnavailablePGsByPeriod(
        newSelectedPeriod.value,
        periodicity,
        pagesConfig.pgsByCountry,
        pagesConfig.masterPgsByCountry,
        appliedCountries,
        appliedProductGroups,
      ).length,
    );

    if (isSomePgsUnavailable > 0) {
      pagesConfig.setIsAllPgsByPeriodicityAvailable(false);
    }
  }, [periodTo, comparisonPeriodTo, periodFrom, comparisonPeriodFrom, periodicity]);

  const focusPeriodKey = useMemo(() => getPeriodKey('focus', periodTo, periodicity), [periodicity, periodTo]);
  const comparisonPeriodKey = useMemo(
    () => getPeriodKey('comparison', comparisonPeriodTo, periodicity),
    [periodicity, comparisonPeriodTo],
  );

  return (
    <div>
      <div className="flex flex-nowrap items-end">
        {[Periodicity.CustomWeeks, Periodicity.CustomMonths].includes(periodicity) && (
          <RangePicker
            key={focusPeriodKey}
            title="Focus Period"
            from={periodFrom}
            to={periodTo}
            disabled={isPeriodOptionsDisabled}
            minDate={minFocusPeriodFrom}
            maxDate={baseTimePeriodToDate(periodOptions[0])}
            setFrom={setPeriodFromHandler}
            setTo={setPeriodToHandler}
            periodicity={periodicity}
          />
        )}
        {isSomePgsUnavailable > 0 ? (
          <div className="mb-2 ml-2">
            <Icon color="secondaryInteractive">
              <Fractional />
            </Icon>
          </div>
        ) : null}
      </div>

      <div className="mt-8">
        {[Periodicity.CustomWeeks, Periodicity.CustomMonths].includes(periodicity) && (
          <RangePicker
            key={comparisonPeriodKey}
            title="Comparison Period"
            from={comparisonPeriodFrom}
            to={comparisonPeriodTo}
            disabled={isPeriodOptionsDisabled}
            minDate={baseTimePeriodToDate(periodOptions[1])}
            maxDate={maxComparisonPeriodTo}
            setFrom={setComparisonPeriodFromHandler}
            setTo={setComparisonPeriodToHandler}
            periodicity={periodicity}
          />
        )}
      </div>
    </div>
  );
};

export default CustomPeriod;
